import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { MessageCircle, X, Send, LoaderCircle } from 'lucide-react';

/* Floating AI support chat: auto-connects every site (platform Gemini key).
 * Renders only when the tenant enabled chat AND a key exists. */
export default function AiChat() {
  const [cfg, setCfg] = useState(null);
  const [open, setOpen] = useState(false);
  const [msgs, setMsgs] = useState([]);
  const [input, setInput] = useState('');
  const [busy, setBusy] = useState(false);
  const bottomRef = useRef(null);

  useEffect(() => {
    axios.get('/ai/chat-config').then(r => setCfg(r.data)).catch(() => {});
  }, []);
  useEffect(() => {
    if (open && msgs.length === 0 && cfg?.greeting) setMsgs([{ role: 'model', text: cfg.greeting }]);
  }, [open, cfg]);
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [msgs, open]);

  if (!cfg || !cfg.enabled) return null;

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;

  const send = async () => {
    const text = input.trim();
    if (!text || busy) return;
    const next = [...msgs, { role: 'user', text }];
    setMsgs(next); setInput(''); setBusy(true);
    try {
      const hist = next.slice(-11, -1).map(m => ({ role: m.role, text: m.text }));
      const r = await axios.post('/ai/chat', { message: text, history: hist });
      setMsgs([...next, { role: 'model', text: r.data.reply }]);
    } catch (e) {
      setMsgs([...next, { role: 'model', text: e.response?.data?.message || 'مشکلی پیش آمد؛ دوباره تلاش کنید.' }]);
    }
    setBusy(false);
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} aria-label={cfg.title || 'گفتگو'}
          style={{ position: 'fixed', bottom: isMobile ? 88 : 20, left: isMobile ? 12 : 20, zIndex: 400, width: 56, height: 56, borderRadius: '50%', border: 'none', background: 'var(--primary, #18181B)', color: 'var(--on-primary, #fff)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 8px 28px rgba(0,0,0,0.28)', fontFamily: 'inherit' }}>
          <MessageCircle size={26} />
        </button>
      )}
      {open && (
        <div role="dialog" aria-label={cfg.title || 'گفتگو'}
          style={{ position: 'fixed', bottom: 20, left: 20, zIndex: 950, width: 'min(370px, calc(100vw - 40px))', height: 'min(520px, calc(100dvh - 60px))', background: 'var(--card, #fff)', color: 'var(--fg, #18181B)', border: '1px solid var(--border, #E4E4E7)', borderRadius: 20, boxShadow: '0 24px 60px rgba(0,0,0,0.25)', display: 'flex', flexDirection: 'column', overflow: 'hidden', direction: 'rtl' }}>
          <div style={{ background: 'var(--primary, #18181B)', color: 'var(--on-primary, #fff)', padding: '13px 16px', display: 'flex', alignItems: 'center', gap: 9, flexShrink: 0 }}>
            <MessageCircle size={19} style={{ flexShrink: 0 }} />
            <span style={{ flex: 1, fontWeight: 800, fontSize: '0.88rem' }}>{cfg.title || 'گفتگو'}</span>
            <button onClick={() => setOpen(false)} aria-label="بستن"
              style={{ width: 30, height: 30, borderRadius: '50%', border: 'none', background: 'rgba(255,255,255,0.16)', color: 'inherit', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}><X size={15} /></button>
          </div>
          <div style={{ flex: 1, overflowY: 'auto', padding: '14px 12px', display: 'flex', flexDirection: 'column', gap: 8 }}>
            {msgs.map((m, i) => (
              <div key={i} style={{ alignSelf: m.role === 'user' ? 'flex-start' : 'flex-end', maxWidth: '85%', background: m.role === 'user' ? 'var(--primary, #18181B)' : 'var(--muted, #F4F4F5)', color: m.role === 'user' ? 'var(--on-primary, #fff)' : 'var(--fg, #18181B)', borderRadius: 14, padding: '9px 13px', fontSize: '0.83rem', lineHeight: 1.9, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                {m.text}
              </div>
            ))}
            {busy && <div style={{ alignSelf: 'flex-end', color: 'var(--muted-fg, #A1A1AA)' }}><LoaderCircle size={18} style={{ animation: 'aiChatSpin 1s linear infinite' }} /><style>{'@keyframes aiChatSpin{to{transform:rotate(360deg)}}'}</style></div>}
            <div ref={bottomRef} />
          </div>
          <div style={{ padding: 10, borderTop: '1px solid var(--border, #E4E4E7)', display: 'flex', gap: 8, flexShrink: 0 }}>
            <input value={input} onChange={e => setInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter') send(); }}
              placeholder="پیامت را بنویس..." disabled={busy}
              style={{ flex: 1, minWidth: 0, padding: '10px 14px', borderRadius: 12, border: '1.5px solid var(--border, #E4E4E7)', fontSize: '0.84rem', outline: 'none', background: 'var(--card, #fff)', color: 'var(--fg, #18181B)', fontFamily: 'inherit' }} />
            <button onClick={send} disabled={busy || !input.trim()} aria-label="ارسال"
              style={{ width: 44, height: 44, borderRadius: 12, border: 'none', background: 'var(--primary, #18181B)', color: 'var(--on-primary, #fff)', cursor: busy || !input.trim() ? 'default' : 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, opacity: busy || !input.trim() ? 0.5 : 1 }}>
              <Send size={18} style={{ transform: 'scaleX(-1)' }} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}
